import moment from "moment";
import { WorkplaceConfig } from "../../../models/attendance"
import { UserAttendanceImageConfig, UserCurrency, UserFaceRecognitionConfig } from "../../../models/general";
import logo from "../../../assets/images/logo.png";
import {
    INIT,
    SET_LOADING,
    SET_FIRST_LOAD,
    SET_PREVURL,
    GET_STATUS,
    ANY_USER_WITH_VARIABLE_SCHEDULE_RULE,
    SET_IMAGE_URL,
    SET_USER_FACE_IMAGE,
    UPDATE_CLOCKING_TIMERS,
    SET_FACE_RECOGNITION_MODELS_LOADED,
    SET_LABELED_FACE_DESCRIPTORS,
    SET_LOCALE,
    SET_NOTIFICATIONS,
    SET_SELECTED_USER,
    SET_SELECTED_USER_MANAGERS,
    SET_USER_AUTH_INFO,
    SET_CURRENT_STATUS_TEXT,
    SET_COMPANY_PRIMARY_COLOR,
    UPDATE_COMPANY_RULES,
    UPDATE_USER_RULES
} from "../actions/userActionCreator";

// Seed initial user-id + token from OIDC storage for the first paint before
// Redux rehydrates. The access token itself is sourced from userAuthInfo — this
// is only a convenience for legacy code paths that still read state.user.token.
let storedToken: string | null = null;
let OIDCUserId: string | null = null;

try {
    const oidcKeys = Object.keys(localStorage).filter(key =>
        key.startsWith('oidc.user:') ||
        (key.startsWith('oidc.') && localStorage.getItem(key)?.includes('access_token'))
    );

    for (const key of oidcKeys) {
        const authData = localStorage.getItem(key);
        if (authData) {
            try {
                const parsedData = JSON.parse(authData);
                if (parsedData && parsedData.access_token) {
                    storedToken = parsedData.access_token;
                    if (parsedData.profile && parsedData.profile.sub) {
                        OIDCUserId = parsedData.profile.sub;
                    }
                    break;
                }
            } catch {
                continue;
            }
        }
    }
} catch (err) {
    console.error("Error retrieving token from localStorage:", err);
}

const iniState = {
    firstLoad: true,
    loading: true,
    userId: OIDCUserId,
    userAuthInfo: null,
    isLoggedIn: false,
    company: null,
    username: "",
    title: "",
    firstName: "",
    lastName: "",
    fullName: "",
    linkedInUrl: "",
    birthDate: moment(new Date()),
    currentStatus: "",
    currentCustomStatusText: "",
    allowedActions: [],
    currentShift: {},
    role: "",
    isManager: false,
    imageUrl: "",
    attendanceImageConfig: UserAttendanceImageConfig.InheritedFromCompany,
    workplaceConfig: WorkplaceConfig.CompanyOrUserSpecific,
    isGCCCountry: false,
    userFaceRecognitionConfig: UserFaceRecognitionConfig.InheritedFromCompany,
    locale: "en",
    rtl: false,
    firstLogin: false,
    userFaceImage: {},
    labeledFaceDescriptors: [],
    companyName: "",
    companyLogo: logo,
    companyLocales: "en,ar,fr",
    token: storedToken || null,
    refreshToken: null,
    companyPrimaryColor: "#304EA1",
    prevUrl: "",
    hireDate: new Date(),
    CompanyCurrencyRate: null,
    companyCurrencyId: 0,
    userCurrencyRule: UserCurrency.InheritedFromCompany,
    UserCurrencyId: 0,
    UserCurrency: null,
    isTimeRunning: false,
    isBreakTimeRunning: false,
    breaksTime: 0,
    totalBreaksTime: 0,
    totalWorkingTime: 0,
    today: null,
    workingTime: 0,
    currentWorkingDuration: 0.5,
    faceRecognitionModelsLoaded: false,
    dayHasMultipleShifts: false,
    fullListNotifications: [],
    notifications: [],
    notificationsTotalCount: 0,
    notificationsTotalUnreadCount: 0,
    selectedUser: undefined,
    selectedUserManagers: [],
    anyUserWithVariableScheduleRule: false,
}

const userReducer = (state = iniState, action: any) => {
    switch (action.type) {

        case INIT:
            if (!action.payload) {
                console.error("INIT action payload is undefined");
                return state;
            }
            return {
                ...state,
                loading: action.payload.loading,
                firstLoad: false,
                userId: action.payload.id,
                isLoggedIn: true,
                company: action.payload.company,
                companyName: action.payload.company?.name || "",
                companyLogo: action.payload.company?.logoUrl || "",
                companyLocales: action.payload.companyLocales,
                companyPrimaryColor: action.payload.company?.primaryColor || undefined,
                username: action.payload.username,
                title: action.payload.title,
                firstName: action.payload.firstName,
                lastName: action.payload.lastName,
                fullName: action.payload.fullName,
                linkedInUrl: action.payload.linkedInUrl,
                birthDate: action.payload.birthDate,
                currentStatus: action.payload.currentStatus,
                currentCustomStatusText: action.payload.currentCustmStatusText,
                allowedActions: action.payload.allowedActions,
                currentShift: action.payload.currentShift,
                role: action.payload.role,
                isManager: action.payload.isManager,
                imageUrl: action.payload.imageUrl,
                attendanceImageConfig: action.payload.attendanceImageConfig,
                workplaceConfig: action.payload.workplaceConfig,
                isGCCCountry: action.payload.isGCCCountry,
                userFaceRecognitionConfig: action.payload.userFaceRecognitionConfig,
                locale: action.payload.locale,
                rtl: action.payload.rtl,
                firstLogin: action.payload.firstLogin,
                userFaceImage: action.payload.userFaceImage,
                labeledFaceDescriptors: action.payload.labeledFaceDescriptors,
                hireDate: action.payload.hireDate,
                CompanyCurrencyRate: action.payload.CompanyCurrencyRate,
                companyCurrencyId: action.payload.companyCurrencyId,
                userCurrencyRule: action.payload.userCurrencyRule,
                UserCurrencyId: action.payload.UserCurrencyId,
                UserCurrency: action.payload.UserCurrency,
                now: moment(new Date()),
                currentWorkingDuration: action.payload.currentWorkingDuration,
                dayHasMultipleShifts: action.payload.dayHasMultipleShifts,
                userRules: action.payload.userRules,
            };
        case SET_USER_AUTH_INFO:
            return {
                ...state,
                userAuthInfo: action.payload,
                isLoggedIn: action.payload ? true : false,
                loading: false,
                token: action.payload?.access_token ?? null
            }
        case SET_LOADING:
            return {
                ...state, loading: action.payload
            }
        case SET_FIRST_LOAD:
            return {
                ...state, firstLoad: action.payload
            }
        case SET_PREVURL:
            return {
                ...state, prevUrl: action.payload
            }
        case GET_STATUS:
            return {
                ...state,
                allowedActions: action.payload.allowedActions,
                currentShift: action.payload.currentShift,
                today: action.payload.today,
                dayHasMultipleShifts: action.payload.dayHasMultipleShifts,
                currentStatus: action.payload.currentStatus ?? state.currentStatus,
                currentCustomStatusText: action.payload.currentCustomStatusText ?? state.currentCustomStatusText,
            };
        case SET_USER_FACE_IMAGE:
            return {
                ...state,
                userFaceImage: action.payload
            }
        case SET_IMAGE_URL:
            return {
                ...state,
                imageUrl: action.payload
            }
        case ANY_USER_WITH_VARIABLE_SCHEDULE_RULE:
            return {
                ...state,
                anyUserWithVariableScheduleRule: action.payload
            }
        case SET_FACE_RECOGNITION_MODELS_LOADED:
            return {
                ...state,
                faceRecognitionModelsLoaded: action.payload
            }
        case SET_LABELED_FACE_DESCRIPTORS:
            return {
                ...state,
                labeledFaceDescriptors: action.payload
            }
        case UPDATE_CLOCKING_TIMERS:
            return {
                ...state,
                breaksTime: action.payload.breaksTime,
                totalBreaksTime: action.payload.totalBreaksTime,
                totalWorkingTime: action.payload.totalWorkingTime,
                workingTime: action.payload.workingTime,
            }
            //case setLocale and update the state
        case SET_LOCALE:
            return{
                ...state,
                locale:action.payload.locale,
                rtl: action.payload.locale === "ar" 
            }
        case SET_NOTIFICATIONS:
            return {
                ...state,
                fullListNotifications: action.payload.fullListNotifications,
                notifications: action.payload.notifications,
                notificationsTotalCount: action.payload.notificationsTotalCount,
                notificationsTotalUnreadCount: action.payload.notificationsTotalUnreadCount
            }
        case SET_SELECTED_USER:
            return {
                ...state,
                selectedUser: action.payload
            }
        case SET_SELECTED_USER_MANAGERS:
            return {
                ...state,
                selectedUserManagers: action.payload
            }
        case SET_CURRENT_STATUS_TEXT:
            return {
                ...state,
                currentCustomStatusText: action.payload
            }
        case SET_COMPANY_PRIMARY_COLOR:
            return {
                ...state,
                companyPrimaryColor: action.payload || state.companyPrimaryColor,
            }
        case UPDATE_COMPANY_RULES:
            // Merge the updated rules into the company object
            const newCompany = state.company ? {
                ...state.company,
                ...action.payload
            } : action.payload;
            return {
                ...state,
                company: newCompany
            }
        case UPDATE_USER_RULES: {
            // Merge the updated per-user rules, and keep the top-level mirror
            // fields (set by INIT) consistent so every consumer sees the change.
            if (!action.payload) return state;
            return {
                ...state,
                userRules: { ...state.userRules, ...action.payload },
                attendanceImageConfig: action.payload.attendanceImageConfig ?? state.attendanceImageConfig,
                userFaceRecognitionConfig: action.payload.faceRecognitionConfig ?? state.userFaceRecognitionConfig,
                workplaceConfig: action.payload.workplaceConfig ?? state.workplaceConfig,
            }
        }
        default:
            return state;
    }
}

export default userReducer;